import * as d3 from 'd3';
import { clamp } from '../../../../shared/utils/minMaxUtils.ts';
import { PreparedData } from '../prepare-data/prepareData.ts';
import { PreparedMeasurement } from '../prepare-data/prepareMeasurements.ts';
import { MeasurementGraphSettings } from '../settings/measurementGraphSettings.ts';
import { MGraphComponents } from './createComponents.ts';
import { lineStyles } from './lineStyles.ts';


type Tooltip = d3.Selection<HTMLDivElement, unknown, d3.BaseType, undefined>;


export function drawData(
    components: MGraphComponents,
    preparedData: PreparedData,
    settings: MeasurementGraphSettings,
) {
    drawControlValues(components, preparedData, settings);
    drawBatchStarts(components, preparedData, settings);
    drawOperationMarkers(components, preparedData, settings);
    drawMeasurements(components, preparedData);
}


function drawControlValues(
    { graph, y }: MGraphComponents,
    { metaData }: PreparedData,
    settings: MeasurementGraphSettings,
) {
    const graphWidth = graph.datum().width;
    const lineThickness = 0.5 + graph.datum().height * 0.004;// should be around 1.5 pixels
    const lines: { value?: number, color: string, dashArray?: string, visibility?: boolean }[] = [
        { value: metaData.nominal, ...lineStyles.nominal, visibility: settings.lineVisibilities.nominal },
        { value: metaData.tolerances[0], ...lineStyles.tolerance, visibility: settings.lineVisibilities.tolerance },
        { value: metaData.tolerances[1], ...lineStyles.tolerance, visibility: settings.lineVisibilities.tolerance },
        { value: metaData.mean, ...lineStyles.mean, visibility: settings.lineVisibilities.mean },
        { value: metaData.sigma1?.[0], ...lineStyles.sigma1, visibility: settings.lineVisibilities.sigma1 },
        { value: metaData.sigma1?.[1], ...lineStyles.sigma1, visibility: settings.lineVisibilities.sigma1 },
        { value: metaData.sigma2?.[0], ...lineStyles.sigma2, visibility: settings.lineVisibilities.sigma2 },
        { value: metaData.sigma2?.[1], ...lineStyles.sigma2, visibility: settings.lineVisibilities.sigma2 },
        { value: metaData.sigma3?.[0], ...lineStyles.sigma3, visibility: settings.lineVisibilities.sigma3 },
        { value: metaData.sigma3?.[1], ...lineStyles.sigma3, visibility: settings.lineVisibilities.sigma3 },
    ];
    for (const line of lines) {
        if (!line.visibility || line.value == null || isNaN(line.value)) continue;
        graph.append('line')
            .attr('x1', 0)
            .attr('y1', y(line.value))
            .attr('x2', graphWidth)
            .attr('y2', y(line.value))
            .attr('stroke-width', lineThickness)
            .attr('stroke', line.color)
            .attr('stroke-dasharray', line.dashArray ?? '0');
    }
}

function drawBatchStarts(
    { graph, x, tooltip }: MGraphComponents,
    { batchStarts }: PreparedData,
    settings: MeasurementGraphSettings,
) {
    if (!settings.lineVisibilities.batchStarts) return;
    if (!batchStarts || batchStarts.length === 0) return;
    const lineThickness = 3 + graph.datum().height * 0.004;// should be around 4 pixels

    for (const batchStart of batchStarts) {
        const xCoordinate = x(batchStart.afterIndex + '')! + (x.step() ?? 0) / 2;
        const line = graph.append('line')
            .attr('class', 'batch-start')
            .attr('x1', xCoordinate)
            .attr('y1', -graph.datum().margins.top)
            .attr('x2', xCoordinate)
            .attr('y2', graph.datum().height)
            .attr('stroke-width', lineThickness)
            .attr('stroke', lineStyles.batchStart.color);

        setTooltip(tooltip, line, batchStart.tooltip);
    }
}

function drawOperationMarkers(
    { graph, x, tooltip }: MGraphComponents,
    { operationMarkers }: PreparedData,
    settings: MeasurementGraphSettings,
) {
    if (!settings.lineVisibilities.operations) return;
    if (!operationMarkers || operationMarkers.length === 0) return;
    const lineThickness = 6 + graph.datum().height * 0.008;// should be around 6 pixels
    const y1 = 0.85 * graph.datum().height;
    const y2 = graph.datum().height;

    for (const operationMarker of operationMarkers) {
        const xCoordinate = x(operationMarker.afterIndex + '')! + (x.step() ?? 0) / 2;
        const line = graph.append('line')
            .attr('class', 'batch-start')
            .attr('x1', xCoordinate)
            .attr('y1', y1)
            .attr('x2', xCoordinate)
            .attr('y2', y2)
            .attr('stroke-width', lineThickness)
            .attr('stroke', lineStyles.operation.color);

        setTooltip(tooltip, line, operationMarker.tooltip);
    }
}

function drawMeasurements(
    { graph, x, y, tooltip }: MGraphComponents,
    { metaData, measurements }: PreparedData,
) {
    type DataPoint = PreparedMeasurement & { index: number };
    const visiblePoints = measurements
        .map((it, index) => ({ ...it, index }))
        .filter(it => it.isDrawn) as DataPoint[];

    //Draw connecting lines between points
    for (let idx = 1; idx < visiblePoints.length; idx++) {
        const curr = visiblePoints[idx];
        const prev = visiblePoints[idx - 1];
        const useRedLine = curr.isHighlighted && prev.isHighlighted;
        const useSolidLine = curr.index - prev.index == 1;//No gap between datapoints

        graph.append('path')
            .datum([curr, prev])
            .attr('fill', 'none')
            .attr('stroke', useRedLine ? '#ff0000' : '#10A37F')
            .attr('stroke-width', useSolidLine ? 3 : 0.6)
            .attr('stroke-dasharray', useSolidLine ? 0 : 5)
            .attr('stroke-dashoffset', useSolidLine ? 0 : 3)
            .attr('d', d3.line<DataPoint>()
                .x((d) => x(d.index + '')!)
                .y((d) => y(clamp(+d.value!, metaData.valueLimits![0], metaData.valueLimits![1])))
            );
    }

    //Draw datapoints
    for (const point of visiblePoints) {
        const circle = graph.append('circle')
            .attr('class', 'measurement')
            .attr('cx', x(point.index + '')! + x.bandwidth() / 2)
            .attr('cy', y(clamp(+point.value!, metaData.valueLimits![0], metaData.valueLimits![1])))
            .attr('r', 5)
            .attr('fill', point.isHighlighted ? '#ff0000' : '#10A37F');

        //Extra detail on hover:
        setTooltip(tooltip, circle, point.tooltip);
    }
}

function setTooltip<Element extends d3.BaseType>(
    tooltip: Tooltip,
    target: d3.Selection<Element, any, any, any>,
    text: string
) {
    target
        .on('mouseover', (_event, d) => {
            tooltip.html(text)
                .style('visibility', 'visible');
        })
        .on('mousemove', (event) => {
            const padding = 10;
            const tooltipWidth = parseInt(tooltip.style('width'), 10);
            const tooltipHeight = parseInt(tooltip.style('height'), 10);

            let xPosition = event.pageX + padding;
            let yPosition = event.pageY - padding;
            if (xPosition + tooltipWidth > window.innerWidth) {// Adjust if tooltip overflows the right edge
                xPosition = event.pageX - tooltipWidth - padding;
            }
            if (yPosition + tooltipHeight > window.innerHeight) { // Adjust if tooltip overflows the bottom edge
                yPosition = event.pageY - tooltipHeight - padding;
            }

            tooltip.style('top', yPosition + 'px')
                .style('left', xPosition + 'px');
        })
        .on('mouseout', () => {
            tooltip.style('visibility', 'hidden');
        });
}
